import ContactForm from 'Helpers/ContactForm';
import axios from 'axios';
import { omit, pick } from 'lodash';
import cookieStore from 'Mixins/cookies.js';
import persistent from '../mixins/persistentContactData';

const defaultState = {
  open: false,
  isStatic: true,
  contactFormType: null,
  contactEnquiryType: null,
  contactSocialType: null,
  agency: null,
  branch: null,
  previousBranch: null,
  branches: {},
  parameters: {},
  error: false,
  formErrors: {},
  loadingBranches: false,
  shouldPreselectBranch: false,
  callingPage: '',
  searchId: null,
  submitting: false,
  success: false,
  shouldRegister: true,
  fakeContactData: {},
  bypassCandidateInformation: true,
  shouldUseExperimentalTelephoneNumberLogic: true,
  sysKey: {},
  isSkipped: false,
  requestorType: null,
  agencyHasNoData: false
};

const mutations = {
  setOpen(state, open = true) {
    state.open = open;
  },
  setSearchId(state, searchId) {
    state.searchId = searchId;
  },
  setSubmitting(state, submitting = true) {
    state.submitting = submitting;
  },
  setError(state, error = true) {
    state.error = error;
  },
  setFormErrors(state, errors) {
    state.formErrors = errors;
  },
  removeFormError(state, key) {
    state.formErrors = omit(state.formErrors, key);
  },
  setLoadingBranches(state, loadingBranches = true) {
    state.loadingBranches = loadingBranches;
  },
  setBranch(state, branch) {
    state.branch = branch;
  },
  setBranches(state, branches) {
    state.branches = branches;
  },
  setPreviousBranch(state, branch) {
    state.previousBranch = branch;
  },
  setShouldPreselectBranch(state, shouldPreselectBranch = true) {
    state.shouldPreselectBranch = shouldPreselectBranch;
  },
  setContactFormType(state, contactFormType) {
    state.contactFormType = contactFormType;
  },
  setContactEnquiryType(state, contactEnquiryType = null) {
    state.contactEnquiryType = contactEnquiryType;
  },
  setContactSocialType(state, contactSocialType = null) {
    state.contactSocialType = contactSocialType;
  },
  setAgency(state, agency) {
    state.agency = agency;
  },
  setShowStageIndicator(state, showStageIndicator = true) {
    state.showStageIndicator = showStageIndicator;
  },
  setCallingPage(state, callingPage) {
    state.callingPage = callingPage;
  },
  setParameter(state, { key, value }) {
    console.log(`* Mutation: contact/setParameter('${key}', '${value}')`);
    if (state.formErrors[`formFields.${key}`]) {
      delete state.formErrors[`formFields.${key}`];
    }
    state.parameters[key] = value;
  },
  setSuccess(state, success = true) {
    state.success = success;
  },
  setShouldRegister(state) {
    state.shouldRegister = true;
  },
  setStatic(state, value) {
    state.isStatic = value;
  },
  flipShouldRegister(state) {
    state.shouldRegister = !state.shouldRegister;
  },
  loadDefaultParameters(state) {
    const emp_cand = state.parameters.emp_cand;
    state.parameters = { ...defaultState.parameters, emp_cand };
    state.agency = null;
    state.branch = null;
    state.contactFormType = null;
    state.contactEnquiryType = null;
    state.error = false;
    state.success = false;
    // TODO: Load in parameters from local storage
  },
  setFakeData(state, fakeData) {
    state.fakeContactData = fakeData;
  },
  setType(state, type) {
    console.log(`!! Contact/setType(${type})`);
    cookieStore.setCookie('requestor_type', type);
    state.requestorType = type;
  },
  setIsSkipped(state, isSkipped = true) {
    state.isSkipped = isSkipped;
  },
  setRequestorType(state, newVal) {
    state.requestorType = newVal;
  },
  setAgencyHasNoData(state, newVal) {
    state.agencyHasNoData = newVal;
  },
};

const getters = {
  currentView(state, getters) {
    // STAGE 1 - USER TYPE
    if (getters.showUserTypeSelectForm) {
      return ContactForm.CONTACT_FORM_USER_TYPE;
    }

    // STAGE 2 - BRANCH SELECT
    if (getters.showBranchSelectForm) {
      return ContactForm.CONTACT_FORM_BRANCH_SELECT;
    }

    // AGENCY HAS NO DATA TO SHOW
    if (getters.showChooseOtherOptions) {
      return ContactForm.CONTACT_CHOOSE_OTHER_OPTION;
    }

    /** STAGE 3 - THE FORM & RESPONSE */

    // CANDIDATES
    if (getters.showCandidateEmailForm) {
      return state.success
        ? ContactForm.CONTACT_FORM_EMAIL_SUCCESS
        : ContactForm.CONTACT_FORM_CANDIDATE_EMAIL;
    }
    if (getters.showCandidateDetailsForm) {
      return state.success
        ? getters.candidateDetailsSuccessForm
        : ContactForm.CONTACT_FORM_CANDIDATE_DETAILS;
    }
    if (getters.showCandidateDetailsSucess) {
        return getters.candidateDetailsSuccessForm;
    }

    // EMPLOYER CLAUSES
    // This logic looks simplified but its hard to follow if you didn't write it.
    if (getters.showEmployerEmailForm) {
      return state.success
        ? ContactForm.CONTACT_FORM_EMAIL_SUCCESS
        : ContactForm.CONTACT_FORM_EMPLOYER_EMAIL;
    }
    if (getters.showEmployerDetailsForm) {
        return ContactForm.CONTACT_FORM_EMPLOYER_DETAILS;
    //   return state.success
    //     ? getters.employerDetailsSuccessForm
    //     : ContactForm.CONTACT_FORM_EMPLOYER_DETAILS;
    }
    if (getters.showEmployerTelfaxSuccess) {
      return ContactForm.CONTACT_FORM_TELFAX_SUCCESS;
    }
    if (getters.showEmployerWebsiteSuccess) {
        return ContactForm.CONTACT_FORM_WEBSITE_SUCCESS;
    }
    console.log("// currentView reached a state it didn't know what to do");
    return null;
  },
  candidateDetailsSuccessForm(state) {
    switch (state.contactFormType) {
      case 'telfax':
        return ContactForm.CONTACT_FORM_TELFAX_SUCCESS
      case 'website':
        return ContactForm.CONTACT_FORM_WEBSITE_SUCCESS
      case 'social':
      case 'mailto':
        return ContactForm.CONTACT_FORM_SOCIAL_SUCCESS
      case 'other':
        return ContactForm.CONTACT_CHOOSE_OTHER_OPTION
    }
  },
  showBranchSelectForm(state, { branchSelected }) {
    return !branchSelected;
  },
  showUserTypeSelectForm(state) {
    console.log('>> Entering showUserTypeSelectForm()');
    const reqType = cookieStore.getCookie('requestor_type');
    console.log(`requestorType: ${reqType}`);

    let param = state.parameters?.emp_cand;
    let cookieTypeValid = ['cnd', 'emp'].includes(reqType);
    let paramValid = ['cnd', 'emp'].includes(param);

    let finalAnswer = param == 'neither' || (!paramValid && !cookieTypeValid);

    console.log(`cookieTypeValid: ${cookieTypeValid}, paramValid: ${paramValid}, finalAnswer: ${finalAnswer}`);

    console.log('>> Leaving showUserTypeSelectForm()');

    return finalAnswer;
  },
  showChooseOtherOptions(state) {
    return state.agencyHasNoData;
  },
  showCandidateEmailForm(state, { isCandidate, branchSelected }) {
    return isCandidate && branchSelected && state.contactFormType === 'email';
  },
  showCandidateDetailsForm(state, { isCandidate, branchSelected, requiresCandidateData, bypassCandidateData }) {
    return (
      isCandidate &&
      branchSelected &&
      (state.contactFormType === 'telfax' ||
        state.contactFormType === 'website' ||
          state.contactFormType === 'social') &&
      requiresCandidateData &&
      !bypassCandidateData
    );
  },
  showCandidateDetailsSucess(state, { isCandidate, branchSelected, requiresCandidateData, bypassCandidateData }) {
    return (
        isCandidate &&
        branchSelected &&
        (state.contactFormType === 'telfax' ||
        state.contactFormType === 'website' ||
            state.contactFormType === 'social' ||
            state.contactFormType === 'mailto') &&
        (bypassCandidateData || !requiresCandidateData)
    );
  },
  showEmployerEmailForm(state, { isEmployer, branchSelected }) {
    return (
        isEmployer && 
        branchSelected && 
        state.contactFormType === 'email'
    )    
  },
  showEmployerDetailsForm(state, { isEmployer, branchSelected, requiresEmployerData, currentView, bypassEmployerData }) {
    // console.log("--- showEmployerDeatilsForm...");
    // console.log("curView: " + currentView);
    // console.log("success: " + state.success);
    // console.log("isEmployer: " + isEmployer);
    // console.log("branchSelected: " + branchSelected);
    // console.log("type: " + state.contactFormType);
    // console.log("bypassEmployerData: " + bypassEmployerData);
    // console.log("reqEmpData: " + requiresEmployerData);
    return (
        !state.success &&
        isEmployer &&
        branchSelected &&
        (state.contactFormType === 'website' || state.contactFormType === 'telfax') &&
        requiresEmployerData
        // && !bypassEmployerData
    );
  },
  employerDetailsFormSuccess(state, { isEmployer, branchSelected, requiresEmployerData, currentView, bypassEmployerData }) {
    console.log(">> Entering employerDeatilsFormSuccess...");
    console.log("curView: " + currentView);
    console.log("success: " + state.success);
    console.log("isEmployer: " + isEmployer);
    console.log("branchSelected: " + branchSelected);
    console.log("type: " + state.contactFormType);
    console.log("bypassEmpData: " + bypassEmployerData);
    console.log("reqEmpData: " + !requiresEmployerData);
    let finalAnswer = (
        // state.success &&
        isEmployer &&
        branchSelected &&
        (state.contactFormType === 'telfax' || state.contactFormType === 'website') &&
        (bypassEmployerData || !requiresEmployerData)
    );
    console.log(`finalAnser: ${finalAnswer}`);
    console.log(">> Leaving employerDeatilsFormSuccess...");
    return finalAnswer;
  },
  showEmployerTelfaxSuccess(state, getters) {
    // console.log("empformsuccess: " + getters.employerDetailsFormSuccess);
    // console.log("type: " + state.contactFormType);
    return (
        getters.employerDetailsFormSuccess &&
        state.contactFormType === 'telfax'
    );
  },
  showEmployerWebsiteSuccess(state, getters) {
    console.log("empformsuccess: " + getters.employerDetailsFormSuccess);
    console.log("type: " + state.contactFormType);    
    return (
        getters.employerDetailsFormSuccess &&
        state.contactFormType === 'website'
    );
  },
  showStageIndicator(state, getters) {
    return getters.stage !== null;
  },
  isEmployer(state, getters) {
    // console.log(`isEmployer -> ${state.parameters.emp_cand}`);
    return state.parameters.emp_cand === 'emp' || cookieStore.getCookie('requestor_type') === 'emp';
  },
  isCandidate(state, getters) {
    return state.parameters.emp_cand === 'cnd' || cookieStore.getCookie('requestor_type') === 'cnd';
  },
  requiresCandidateData(state, { inForm }) {    
    //console.log(`${state.parameters.firstName} / ${state.parameters.surname} / ${state.parameters.jobTitle}`);
    return !(
        !inForm && 
        state.parameters.firstName && 
        state.parameters.surname &&
        state.parameters.jobTitle
    );
  },
  requiresEmployerData(state, { inForm }) {
    // console.log("inForm: " + !inForm);
    // console.log("employerName: " + state.parameters.employerName);
    let a = !(
        !inForm &&
        state.parameters.employerName &&
        state.parameters.employerName !== 'null'
    );
    // console.log("result: " + a);
    return a;
  },
  branchSelected(state) {
    return state.branch !== null;
  },
  stage(state, getters) {
    const currentView = getters.currentView;
    if (ContactForm.STAGE_1_VIEW.includes(currentView)) {
      return 1;
    }
    if (ContactForm.STAGE_2_VIEW.includes(currentView)) {
      return 2;
    }
    if (ContactForm.STAGE_3_VIEW.includes(currentView)) {
      return 3;
    }
    return null;
  },
  fullUserType(state, getters) {
    if (state.parameters.emp_cand === 'emp' || cookieStore.getCookie('requestor_type') === 'emp') {
      return 'employer';
    }
    if (state.parameters.emp_cand === 'cnd' || cookieStore.getCookie('requestor_type') === 'cnd') {
      return 'candidate';
    }
    return null;
  },
  inForm(state, getters) {
    const view = getters.currentView;
    //console.log(`inForm view = ${view}`);
    return !state.success && Object.keys(ContactForm.CONTACT_FORM_TYPES).includes(view);
  },
  formName(state, getters) {
    const view = getters.currentView;
    console.log(`formName/view = ${view}`);
    console.log(`cfType = ${state.contactFormType}`);

    // let reqType = state.parameters.emp_cand;
    // let cookieType = cookieStore.getCookie('requestor_type');
    // let actType = reqType;

    // if (reqType == 'neither' && ['cnd','emp'].includes(cookieType)) {
    //     actType = cookieType;
    // }

    let cf = ContactForm.CONTACT_FORM_TYPES[view];
    console.log(`cf: ${cf}`);

    if (cf == undefined) {
        cf = (getters.isCandidate)
            ? ContactForm.CONTACT_FORM_FOR_CND[view]
            : ContactForm.CONTACT_FORM_FOR_EMP[view]
            ;
    }

    console.log(`Determined formName: ${cf}`);
    return cf;
  },
  contactFormType(state) {
    return state.contactFormType;
  },
  contactEnquiryType(state) {
    return state.contactEnquiryType;
  },
  shouldShowSkipButton(state, { isEmployer }) {
    console.log('>> Entering shouldShowSkipbutton');
    console.log(`-- agency.FAL: ${state.agency.FAL}`);
    console.log(`-- contactFormType: ${state.contactFormType}`);
    let clause = false;

    // if (state.agency.FAL === true) {
    //   clause = isEmployer
    //       && state.contactFormType == 'telfax';
    // } else
    if (state.agency.FAL === false) {
      clause =
          (state.shouldUseExperimentalTelephoneNumberLogic
          && state.contactFormType === 'telfax'
        //   && state.isStatic === false
          && isEmployer
        //   && !getters.isBusinessHour // removed 07/04/2025
        //   && state.agency.hasOOH === 1
        )
    }

    console.log('-- return: ' + !clause);
    console.log('>> Entering shouldShowSkipbutton');
    return clause;
  },
  bypassEmployerData(state, { isEmployer }) {
    console.log(">>> Entering bypassEmployerData");
    console.log(`isEmployer: ${isEmployer}`);
    console.log(`contactFormType: ${state.contactFormType}`);
    console.log(`experimentalTelLogic: ${state.shouldUseExperimentalTelephoneNumberLogic}`);
    console.log(`isBusinessHouse: ${getters.isBusinessHour()}`);
    console.log(`agency.OOH ${state.agency.hasOOH}`);
    console.log(`agency.FAL: ${state.agency.FAL}`);
    let finalAnser = (
        isEmployer &&
        (
            state.contactFormType === 'telfax'
            // && state.shouldUseExperimentalTelephoneNumberLogic 
            // && (getters.isBusinessHour || (!getters.isBusinessHour && state.agency.hasOOH)) 
        )
        // ||
        // (
        //     state.contactFormType === 'website'
        // )
        && !state.agency.FAL
    );

    console.log(`finalAnswer: ${finalAnser}`);
    console.log(">>> Leaving bypassEmployerData");
    return finalAnser;
  },
  bypassCandidateData(state, {isCandidate}) {
    return (
        isCandidate &&
        (state.contactFormType === 'telfax' ||
            state.contactFormType === 'website' ||
            state.contactFormType === 'social' ||
            state.contactFormType === 'mailto')
        // && state.shouldUseExperimentalTelephoneNumberLogic
        // && (getters.isBusinessHour || (!getters.isBusinessHour && state.agency.hasOOH))
    );
  },
  isBusinessHour() {
    var o = new Date();
    var dow = o.getDay();
    var t = (o.getHours() * 60) + o.getMinutes();

    // we are closed Saturday & Sunday
    if (dow == 6 || dow == 0) {
      return false;
    }

    // business hours 8:30am to 17:30
    if (t >= 510 && t <= 1050) {
      return true;
    }

    // any other time we're shut
    return false;
  },
};

const actions = {
  setAgency({ state, commit, dispatch }, agency) {
    commit('setAgency', agency);
    dispatch('getBranchesForAgency');
  },

  bypassCandidateForm({ state, commit, dispatch }) {
    console.log(">> Entering bypassCandidateForm()");
    const candidateCookie = cookieStore.getCookie('candidate-first-name');
    console.log(`-- candidateCookie: ${candidateCookie}`);
    console.log(`-- bypassCandidateInformation: ${state.bypassCandidateInformation}`);
    console.log(`-- fakeContactData.length: ${state.fakeContactData.length}`);

    if (!candidateCookie &&
        state.bypassCandidateInformation &&
        state.fakeContactData.length !== 0
    ) {
      commit('setParameter', {key: 'firstName', value: state.fakeContactData.firstName});
      commit('setParameter', {key: 'surname', value: state.fakeContactData.surname});
      commit('setParameter', {key: 'emailAddress', value: state.fakeContactData.emailAddress});
      commit('setParameter', {key: 'jobTitle', value: state.fakeContactData.jobTitle});
      commit('setParameter', {key: 'telephoneNumber', value: state.fakeContactData.telephoneNumber});
    }
    dispatch('registerContact');
    console.log(">> Leaving bypassCandidateForm()");
  },
  registerContact({ state, commit, getters, dispatch }) {
    console.log('BEGIN registerContact()');

    // prevent double registering as we transition from form to success
    const shouldRegister = state.shouldRegister;
    const candidateCookie = cookieStore.getCookie('candidate-first-name');

    commit('flipShouldRegister');
    if (shouldRegister === false) {
        console.log('** EXIT registerContact this time to prevent double register');
        return;
    }

    commit('setSubmitting');
    commit('setFormErrors', {});
    commit('setError', false);

    console.log(`Email address: ${state.parameters.emailAddress}`);

    if ((getters.isCandidate && !getters.bypassCandidateData) || (getters.isEmployer)) {
      dispatch('storePersistentInformation');
    }

    if (getters.isCandidate 
        && !candidateCookie
        && state.contactFormType != 'email'
        && state.bypassCandidateInformation
        && state.fakeContactData.length !== 0
    ) {
      commit('setParameter', {key: 'firstName', value: state.fakeContactData.firstName});
      commit('setParameter', {key: 'surname', value: state.fakeContactData.surname});
      commit('setParameter', {key: 'emailAddress', value: state.fakeContactData.emailAddress});
      commit('setParameter', {key: 'jobTitle', value: state.fakeContactData.jobTitle});
      commit('setParameter', {key: 'telephoneNumber', value: state.fakeContactData.telephoneNumber});
    }

    console.log(state.parameters.emailAddress);

    const url = '/api/contact/register';
    const formData = new FormData();
    formData.append('form-name', getters.formName);
    formData.append('action', state.contactFormType);
    formData.append('user-type', getters.fullUserType);
    formData.append('calling-page', state.callingPage);
    formData.append('search-id', state.searchId);
    formData.append('agency', state.agency.AgencyTag);
    formData.append('branch', state.branch.branch);

    if (state.contactFormType == 'social') {
        formData.append('user-platform-type', state.contactSocialType);
    } else if (state.contactFormType == 'mailto') {
        formData.append('user-platform-type', 'mailto');
    }
    
    for (let [key, value] of Object.entries(state.parameters)) {
        if (value === null) {
            value = '';
        }
        formData.append(`formFields[${key}]`, value);
    }

    console.log(`contactFormType = ${state.contactFormType}`);
    console.log(`contactEnquiryType = ${state.contactEnquiryType}`);
    if (getters.isEmployer && state.contactFormType === 'email') {
        const enquiryType = (state.contactEnquiryType !== null) 
            ? state.contactEnquiryType
            : 'enquiry';
        formData.append('user-enquiry-type', enquiryType);
    }

    console.log(Object.fromEntries(formData));

    return axios
      .post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Accept: 'application/json',
        },
      })
      .then((response) => {
        commit('setSuccess');
        if (typeof(jQuery) !== 'undefined') {
            jQuery(document).trigger('tc_contact_success', getters.formName);
        }
        return response;
      })
      .catch((error) => {
        commit('setShouldRegister', true);
        if (error.response.status !== 422) {
          commit('setError');
          commit('setFormErrors', {
            error: ['An error occurred submitting your form.'],
          });
        } else {
          commit('setFormErrors', error.response.data.errors);
        }
        throw error;
      })
      .finally(() => {
        commit('setSubmitting', false);
        console.log('END registerContact()');
      });
  },
  preregisterContact({ state, commit, getters }) {
    const url = '/api/contact/preregister';
    const data = {
      'form-name': 'EmployerInformation',
      action: state.contactFormType,
      'user-type': getters.fullUserType,
      'calling-page': state.callingPage,
      'search-id': state.searchId,
      agency: state.agency.AgencyTag,
      branch: state.branch.branch,
    };

    axios
      .post(url, data)
      .then((response) => {
        // Do nothing
      })
      .catch((error) => {
        // Do Nothing
      })
      .finally(() => {
        // Do nothing
      });
  },
  skipContactForm({ state, commit, getters, dispatch }, agency) {
    if (getters.isBusinessHour) {
//      dispatch('preregisterContact');
      commit('setIsSkipped', true);
      commit('setSuccess');
      commit('setSubmitting', false);
    } else {
//      dispatch('preregisterContact');
      commit('setIsSkipped', true);
      commit('setSuccess');
      commit('setSubmitting', false);
    }
  },
  getBranchesForAgency({ state, commit }) {
    commit('setLoadingBranches');
    commit('setBranches', null);
    const url = `/api/agencyBranches/${state.agency.AgencyTag}`;
    axios
      .get(url)
      .then(
        (response) => {
          commit('setBranches', response.data.data.branches);

          // Preselect the first branch in an agencies list of branches if the search
          // includes a location as the branches are sorted by distance.
          if (state.shouldPreselectBranch) {
            // Get first branch from agency
            const firstBranch = state.agency.branches[0];
            commit('setBranch', {
              town: firstBranch.Town,
              branch: firstBranch.Tag,
            });
          }
        },
        (error) => {
          commit('setError');
        }
      )
      .finally(() => {
        commit('setLoadingBranches', false);
      });
  },
  loadPersistentInformation({state, commit, getters}, specificFormName) {
    console.log(">> Entering loadPersistentInformation:");
    const keys = persistent.getKeysForForm(specificFormName || getters.formName || getters.currentView);
    for (let [paramName, storeName] of Object.entries(keys)) {
        const val = cookieStore.getCookie(storeName);
        if (val != null || val != "null") {
            //console.log(`Restoring ${storeName} with value '${val}' into ${paramName}`);
            commit('setParameter', { key: paramName, value: val});
        } else {
            //console.log(`Skipped ${storeName} as value was null`);
        }
    }
    console.log(">> Leaving loadPersistentInformation");
  },
  storePersistentInformation({state, commit, getters}) {
    console.log("storePersistentInformation:");
    const keys = persistent.getKeysForForm(getters.formName);
    console.log(keys);
    for (let [paramName, storeName] of Object.entries(keys)) {
        const val = state.parameters[paramName];
        console.log(`Storing ${paramName} with value '${val}' into ${storeName}`);
        cookieStore.setCookie(storeName, val);
    }
  },
  updateFakeData({ state, commit }, newState) {
    commit('setFakeData', newState.fakeContactData);
  },
  triggerTrackingEvent({ state }) {
    let t = 'tc_contact_' + state.contactFormType.toLowerCase();

    if (typeof(jQuery) !== 'undefined') {
        jQuery(document).trigger(t, { 
            form_type: state.contactEnquiryType, 
            user_type: state.parameters.emp_cand 
        });
    }
  },
  initUserType({ commit }) {
    const savedType = cookieStore.getCookie('requestor_type');
    commit('setRequestorType', savedType || null);
  },
};

export default {
  namespaced: true,
  state: defaultState,
  mutations,
  getters,
  actions,
};
